'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { createLDReactProvider, useLDClient, type LDContext } from '@launchdarkly/react-sdk';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/toaster';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { DrawerStackProvider } from '@/components/ui/drawer-stack-context';
import { RumInit } from '@/components/observability/rum-init';
import { RumUser } from '@/components/observability/rum-user';
import { AuthTransitionProvider } from '@/lib/auth/auth-transition';
import { isGuestPath } from '@/lib/auth/guest-paths';
import { TransitionLogo } from '@/components/layout/transition-logo';
import { MswProvider } from '@/mocks/msw-provider';
import { CompanyDateFormatProvider } from '@/lib/utils/format-date';
import { useCurrentUser, type CurrentUser, type ProfileCompany } from '@/lib/data/profile';
import { CurrentUserIdProvider } from '@/lib/data/current-user-context';
import {
  DEFAULT_INTEGRATIONS_FLAG_STATE,
  IntegrationsFlagProvider,
  useLaunchDarklyIntegrationsFlag,
  type IntegrationsFlagState,
} from '@/lib/flags/integrations';
import {
  CHANGE_SATURATION_FLAG_LOADING,
  CHANGE_SATURATION_FLAG_OFF,
  ChangeSaturationFlagProvider,
  changeSaturationEnvOverride,
  useLaunchDarklyChangeSaturationFlag,
  type ChangeSaturationFlagState,
} from '@/lib/flags/change-saturation';

type LDProviderComponent = ReturnType<typeof createLDReactProvider>;
type LDProviderRecord = {
  Provider: LDProviderComponent;
  initialContextSignature: string;
};

const ldProviderCache = new Map<string, LDProviderRecord>();

/**
 * Whether a failed query/mutation should raise a toast.
 *
 * `meta: { errorHandler: 'silent' }` is the opt-out, the existing convention
 * used by call sites whose failure is either inconsequential (the setup overview
 * keeps its last value) or surfaced some other way (document analysis renders
 * a banner). Only the exact string `'silent'` silences; any other value toasts.
 */
export function shouldToastError(meta: Record<string, unknown> | undefined): boolean {
  return meta?.errorHandler !== 'silent';
}

function reportError(error: unknown): void {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  toast.error(message);
}

type LdCompany = { key: string; name?: string };

// Resolve the company to include in the LaunchDarkly context. A local-dev
// override (NEXT_PUBLIC_LD_COMPANY_KEY) links this machine's user to a specific
// company for targeting without the backend having to surface it yet; otherwise
// use the company from /profile once it's available. Returns null when neither
// is present, in which case LD receives a user-only context (per-company flags
// stay off).
export function resolveLdCompany(profileCompany?: ProfileCompany): LdCompany | null {
  const overrideKey = process.env.NEXT_PUBLIC_LD_COMPANY_KEY;
  if (overrideKey) {
    const name = process.env.NEXT_PUBLIC_LD_COMPANY_NAME;
    return name ? { key: overrideKey, name } : { key: overrideKey };
  }
  if (profileCompany?.id) {
    return profileCompany.name
      ? { key: profileCompany.id, name: profileCompany.name }
      : { key: profileCompany.id };
  }
  return null;
}

export function getLaunchDarklyContext(user: CurrentUser, company: LdCompany | null): LDContext {
  const userContext = {
    key: user.id,
    email: user.email,
    name: `${user.firstName} ${user.lastName}`.trim(),
  };
  if (!company) {
    return { kind: 'user', ...userContext };
  }
  // Multi-context: LD evaluates the `company` kind for per-company targeting.
  return {
    kind: 'multi',
    user: userContext,
    company,
  } as LDContext;
}

function getContextSignature(context: LDContext) {
  return JSON.stringify(context);
}

function getLDProvider(clientSideId: string, context: LDContext): LDProviderRecord {
  const cached = ldProviderCache.get(clientSideId);
  if (cached) {
    return cached;
  }

  const record = {
    Provider: createLDReactProvider(clientSideId, context),
    initialContextSignature: getContextSignature(context),
  };
  ldProviderCache.set(clientSideId, record);
  return record;
}

function LaunchDarklyFlagBridge({
  context,
  initialContextSignature,
  onFlagChange,
  onSaturationFlagChange,
}: {
  context: LDContext;
  initialContextSignature: string;
  onFlagChange: (state: IntegrationsFlagState) => void;
  onSaturationFlagChange: (state: ChangeSaturationFlagState) => void;
}) {
  const client = useLDClient();
  const flagState = useLaunchDarklyIntegrationsFlag();
  const saturationFlag = useLaunchDarklyChangeSaturationFlag();
  const contextSignature = useMemo(() => getContextSignature(context), [context]);
  const lastIdentifiedSignature = useRef(initialContextSignature);

  useEffect(() => {
    onFlagChange(flagState);
  }, [flagState.enabled, flagState.error, flagState.isLoading, onFlagChange]);

  useEffect(() => {
    onSaturationFlagChange(saturationFlag);
  }, [saturationFlag.enabled, saturationFlag.isLoading, onSaturationFlagChange]);

  useEffect(() => {
    if (contextSignature === lastIdentifiedSignature.current) {
      return;
    }

    let canceled = false;
    lastIdentifiedSignature.current = contextSignature;
    client.identify(context).catch((error: unknown) => {
      if (canceled) {
        return;
      }

      onFlagChange({
        enabled: false,
        isLoading: false,
        error: error instanceof Error ? error : new Error('LaunchDarkly identify failed'),
      });
    });

    return () => {
      canceled = true;
    };
  }, [client, context, contextSignature, onFlagChange]);

  return null;
}

function LaunchDarklyGate({ children }: { children: ReactNode }) {
  const gate = <LaunchDarklyIntegrationsGate>{children}</LaunchDarklyIntegrationsGate>;
  // The Change saturation report's own escape hatch, independent of the integrations one.
  if (changeSaturationEnvOverride()) {
    return (
      <ChangeSaturationFlagProvider value={{ enabled: true, isLoading: false }}>
        {gate}
      </ChangeSaturationFlagProvider>
    );
  }
  return gate;
}

function LaunchDarklyIntegrationsGate({ children }: { children: ReactNode }) {
  if (process.env.NEXT_PUBLIC_INTEGRATIONS_ENABLED === 'true') {
    return (
      <IntegrationsFlagProvider value={{ enabled: true, isLoading: false, error: null }}>
        {children}
      </IntegrationsFlagProvider>
    );
  }

  const clientSideId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID;
  // When LaunchDarkly is not configured (dev, tests), do no work: provide the
  // default (disabled) flag state and skip the profile fetch entirely.
  if (!clientSideId) {
    return (
      <IntegrationsFlagProvider value={DEFAULT_INTEGRATIONS_FLAG_STATE}>
        {children}
      </IntegrationsFlagProvider>
    );
  }
  return <LaunchDarklyGateActive clientSideId={clientSideId}>{children}</LaunchDarklyGateActive>;
}

function LaunchDarklyGateActive({
  clientSideId,
  children,
}: {
  clientSideId: string;
  children: ReactNode;
}) {
  // Guest pages have no session, so /profile would 401. Flags aren't needed
  // there either — skip the fetch until the user is on an authenticated route.
  const pathname = usePathname();
  const profileQuery = useCurrentUser({ enabled: !isGuestPath(pathname) });
  const [flagState, setFlagState] = useState<IntegrationsFlagState>(
    DEFAULT_INTEGRATIONS_FLAG_STATE,
  );
  // Loading until the flags arrive, so the report never flashes "not available" first.
  const [saturationFlag, setSaturationFlag] = useState<ChangeSaturationFlagState>(
    CHANGE_SATURATION_FLAG_LOADING,
  );
  const ldContext = useMemo(() => {
    const user = profileQuery.data?.user;
    if (!user) return null;
    const company = resolveLdCompany(profileQuery.data?.company);
    return getLaunchDarklyContext(user, company);
  }, [
    profileQuery.data?.user?.email,
    profileQuery.data?.user?.firstName,
    profileQuery.data?.user?.id,
    profileQuery.data?.user?.lastName,
    profileQuery.data?.company?.id,
    profileQuery.data?.company?.name,
  ]);
  const ldProvider = ldContext ? getLDProvider(clientSideId, ldContext) : null;
  const LDProvider = ldProvider?.Provider;

  // Fall back to the default (disabled) flag state whenever there is no LD
  // context. `LaunchDarklyFlagBridge` is the only thing that reports real flag
  // values, and it only renders alongside a context — so without this the last
  // value it pushed would linger after a logout. Adjusted during render rather
  // than in an effect (per React's "you might not need an effect", which is
  // also what react-hooks/set-state-in-effect asks for); the comparison is
  // against the initial state's own reference, so this settles in one pass.
  if (!ldContext && flagState !== DEFAULT_INTEGRATIONS_FLAG_STATE) {
    setFlagState(DEFAULT_INTEGRATIONS_FLAG_STATE);
  }
  // No context once the profile has settled (signed out, or no user): the report is off.
  if (!ldContext && !profileQuery.isLoading && saturationFlag !== CHANGE_SATURATION_FLAG_OFF) {
    setSaturationFlag(CHANGE_SATURATION_FLAG_OFF);
  }

  // An env override above wins: this provider then sits inside it and is ignored.
  const saturation = changeSaturationEnvOverride()
    ? { enabled: true, isLoading: false }
    : saturationFlag;
  return (
    <IntegrationsFlagProvider value={flagState}>
      <ChangeSaturationFlagProvider value={saturation}>
        {LDProvider && ldContext && ldProvider ? (
          <LDProvider>
            <LaunchDarklyFlagBridge
              context={ldContext}
              initialContextSignature={ldProvider.initialContextSignature}
              onFlagChange={setFlagState}
              onSaturationFlagChange={setSaturationFlag}
            />
          </LDProvider>
        ) : null}
        {children}
      </ChangeSaturationFlagProvider>
    </IntegrationsFlagProvider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (shouldToastError(query.meta)) reportError(error);
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _vars, _ctx, mutation) => {
            if (shouldToastError(mutation.meta)) reportError(error);
          },
        }),
        defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
      }),
  );
  return (
    <MswProvider>
      <RumInit />
      <QueryClientProvider client={client}>
        <RumUser />
        <LaunchDarklyGate>
          {/* Inside the QueryClientProvider — it reads `company` once and
              publishes the tenant's date format to every date cell below. */}
          <CompanyDateFormatProvider>
            {/* Same reasoning, for the signed-in user's id — lets Table scope
                its persisted column layout/sort per user without every table
                instance needing its own profile fetch. */}
            <CurrentUserIdProvider>
              <AuthTransitionProvider>
                <BaseTooltip.Provider>
                  {/* Above ConfirmProvider (which wraps its own ModalProvider — see
                      confirm-dialog.tsx) so `Drawer` (mounted lower, inside
                      `(main)/layout.tsx`) and `Modal`/`useConfirm` share ONE overlay
                      stack instance. A `Drawer` opened first and a confirm dialog
                      raised from inside it (e.g. `useConfirm` in
                      audiences/org-groups/membership-panel.tsx) need to agree on
                      which of them is topmost; they only can if both register with
                      the same `DrawerStack`, which requires this provider to sit
                      above both rather than nested only under `(main)/layout.tsx`
                      (where it used to live — see that file's own comment). */}
                  <DrawerStackProvider>
                    <ConfirmProvider>{children}</ConfirmProvider>
                  </DrawerStackProvider>
                </BaseTooltip.Provider>
                <TransitionLogo />
              </AuthTransitionProvider>
            </CurrentUserIdProvider>
          </CompanyDateFormatProvider>
        </LaunchDarklyGate>
        <Toaster />
      </QueryClientProvider>
    </MswProvider>
  );
}
